import type { FormItem } from "./form-builder.model";
import type { FormAction } from "./renderer/form-renderer";
import { datasetFormRequest } from "./dataset-form-signal";
import { notify } from "../notifications/NotificationProvider";
import { ViewManager } from "../../services/view-manager";
import { adminRpc } from "../../services/admin-rpc-client";
import {
  DIALECTS,
  listDatabases,
  saveDatabase,
  testDatabase,
  updateDatabaseJob,
  type DatabaseConnection,
} from "../../services/database-admin-rpc-client";

export type DatabaseFormOpts = {
  /** database_connection uid to edit; undefined = new */
  key?: string;
  /** a view to re-fetch after a save (the page's list) */
  refreshView?: string;
};

const URL_HELP: Record<string, string> = {
  trino: "http[s]://user@host:port — the user only; the password goes below (and needs https)",
  postgres: "postgresql://user@host:5432/dbname?sslmode=require — no password in the url",
  mysql: "mysql://user@host:3306/dbname — MySQL or MariaDB; no password in the url",
  sqlserver: "sqlserver://user@host:1433?database=dbname — no password in the url",
};

/** The mesh itself, as the "Runs on" choice: no worker, no job. */
export const RUNS_ON_MESH = "";

/**
 * The workers a database may run on, as select options after the mesh: every
 * worker, labelled login@server. The server refuses one that is not active.
 */
export async function runsOnOptions(): Promise<{ value: string; text: string }[]> {
  const r = await adminRpc.query(
    "from worker w join server s on s.uid = w.serveruid select w.uid, w.login || '@' || s.name as label",
  );
  const workers = r.rows
    .map((row) => ({ value: String(row["uid"] ?? ""), text: String(row["label"] ?? "") }))
    .filter((o) => o.value)
    .sort((a, b) => a.text.localeCompare(b.text));
  return [{ value: RUNS_ON_MESH, text: "The mesh" }, ...workers];
}

/**
 * The database registry form (continuum.database_connection), registered as
 * "database" in the page sdk's FORMS. Not a dataset form: the row is written by
 * dbaccess.v1.SaveDatabase, which encrypts the password server-side, so the
 * password is never a column the client writes and never comes back.
 *
 * "Test connection" is a form action: it tests what is TYPED, password included,
 * without saving, from wherever "Runs on" says (a worker's job-runner opens the
 * connection on its own host). "Test before saving" asks the server to run the
 * same test and refuse the save if it fails.
 *
 * "Runs on" chooses the worker whose job-runner runs this database's queries, or
 * the mesh. The server owns the job: saving creates, moves or retires it and
 * restarts it when the connection changed; "Update job" does the same and always
 * restarts (FEATURE-20261001-database-connection-executed-by-a-worker-daemon).
 */
export async function openDatabaseForm(opts: DatabaseFormOpts): Promise<void> {
  let existing: DatabaseConnection | undefined;
  if (opts.key) {
    try {
      existing = (await listDatabases()).find((d) => d.uid === opts.key);
    } catch (e) {
      notify({ severity: "error", title: "Could not load the database", message: String(e) });
      return;
    }
    if (!existing) {
      notify({ severity: "error", title: "Not found", message: `No database ${opts.key}.` });
      return;
    }
  }
  const owned = Boolean(existing?.modelSource);

  const items: FormItem[] = [
    {
      name: "name",
      label: "Name",
      type: "text",
      required: true,
      disabled: owned,
      helpText: "What callers pass as database: lowercase letters, digits, _ or -",
    },
    {
      name: "dialect",
      label: "Dialect",
      type: "select",
      required: true,
      disabled: owned,
      options: DIALECTS.map((d) => ({ value: d, text: d })),
    },
    {
      name: "url",
      label: "URL",
      type: "text",
      required: true,
      disabled: owned,
      helpText:
        "Trino: http[s]://user@host:port. Postgres: postgresql://user@host:5432/db. MySQL: mysql://user@host:3306/db. SQL Server: sqlserver://user@host:1433?database=db. No password in the url.",
    },
    {
      name: "password",
      label: "Password",
      type: "password",
      disabled: owned,
      helpText: existing?.hasPassword
        ? "A password is stored (encrypted). Leave blank to keep it."
        : "Stored encrypted; never shown again.",
    },
    ...(existing?.hasPassword
      ? ([
          {
            name: "clear_password",
            label: "Remove the stored password",
            type: "toggle",
            disabled: owned,
          },
        ] as FormItem[])
      : []),
    { name: "description", label: "Description", type: "text", disabled: owned },
    {
      name: "worker",
      label: "Runs on",
      type: "select",
      disabled: owned,
      options: runsOnOptions,
      helpText:
        "The mesh opens the connection itself. A worker runs a job (a8 db-serve) next to the database and the mesh forwards every statement to it; the worker needs a job-runner.",
    },
    {
      name: "execution_user",
      label: "Runs as",
      type: "text",
      disabled: owned,
      helpText: "The account the job runs as on that worker. Blank: the worker's own login. Only for a worker.",
    },
    ...(owned
      ? []
      : ([{ name: "test_first", label: "Test before saving", type: "toggle" }] as FormItem[])),
  ];

  const testAction: FormAction = {
    label: "Test connection",
    run: async (values) => {
      const r = await testDatabase({
        uid: existing?.uid,
        dialect: String(values["dialect"] ?? ""),
        url: String(values["url"] ?? ""),
        password: String(values["password"] ?? "") || undefined,
        workerUid: String(values["worker"] ?? RUNS_ON_MESH),
      });
      return r.ok
        ? {
            severity: "success",
            message: `Connected in ${r.elapsedMillis} ms${r.serverVersion ? ` — ${r.serverVersion}` : ""}`,
          }
        : { severity: "error", message: `Connection failed: ${r.error}` };
    },
  };

  // Update job: the saved row's job, brought in line and restarted. Only a database
  // that has one; a new worker is chosen and saved first.
  const updateJobAction: FormAction = {
    label: "Update job",
    run: async () => {
      const r = await updateDatabaseJob(existing?.uid ?? "");
      if (opts.refreshView) ViewManager.get().refreshView(opts.refreshView);
      return r.success
        ? { severity: "success", message: r.message || "job updated" }
        : { severity: "error", message: r.error };
    },
  };

  const runsOn = existing?.jobUid
    ? ` — runs on ${existing.workerLabel || existing.workerUid}${existing.jobLive ? "" : " (job not running)"}`
    : "";
  const subtitle = (owned ? ` (owned by ${existing?.modelSource}; change it there)` : "") + runsOn;

  datasetFormRequest.next({
    title: (existing ? existing.name : "New database") + subtitle,
    sections: [{ title: "", items }],
    initialValues: {
      name: existing?.name ?? "",
      dialect: existing?.dialect ?? "trino",
      url: existing?.url ?? "",
      password: "",
      clear_password: false,
      description: existing?.description ?? "",
      worker: existing?.workerUid ?? RUNS_ON_MESH,
      execution_user: existing?.executionUser ?? "",
      test_first: true,
    },
    validate: (values) =>
      URL_HELP[String(values["dialect"])] && !String(values["url"] ?? "").trim()
        ? [{ fields: ["url"], severity: "error", message: `URL is required: ${URL_HELP[String(values["dialect"])]}` }]
        : [],
    submitLabel: "Save",
    actions: existing?.jobUid && !owned ? [testAction, updateJobAction] : [testAction],
    onCancel: () => datasetFormRequest.next(undefined),
    onSubmit: async (values) => {
      if (owned) {
        notify({ severity: "warning", title: "Read-only here", message: `Change ${existing?.name} in its model file.` });
        return;
      }
      const res = await saveDatabase({
        uid: existing?.uid,
        name: String(values["name"] ?? "").trim(),
        dialect: String(values["dialect"] ?? ""),
        url: String(values["url"] ?? "").trim(),
        description: String(values["description"] ?? ""),
        password: String(values["password"] ?? "") || undefined,
        clearPassword: Boolean(values["clear_password"]),
        credentialRef: existing?.credentialRef || undefined,
        testFirst: Boolean(values["test_first"]),
        // Always sent: the form shows the choice, so what it shows is what is saved.
        workerUid: String(values["worker"] ?? RUNS_ON_MESH),
        executionUser: String(values["execution_user"] ?? "").trim(),
      });
      if (!res.success || !res.database) {
        notify({ severity: "error", title: "Not saved", message: res.error || "save failed" });
        return;
      }
      notify({
        severity: "success",
        title: "Saved",
        message: res.jobMessage ? `Saved ${res.database.name}; ${res.jobMessage}` : `Saved ${res.database.name}`,
      });
      if (opts.refreshView) ViewManager.get().refreshView(opts.refreshView);
      // Reopen on the saved row, so the form now shows it as stored.
      await openDatabaseForm({ key: res.database.uid, refreshView: opts.refreshView });
    },
  });
}
